// Pacific & Orient on the add-ons step (VULN-04 residual, 27 Sep 2026).
//
// Every other insurer is re-quoted by its own /api/motor/get-quote-<insurer> endpoint before
// checkout. Pacific & Orient used to fall into the add-ons page's last branch instead, which
// priced it in the browser from the listed card:
//
//   (basic_premium - ncd_amount + add-on "sum_insured" values) x 1.08 + RM10
//
// That figure leaves out everything P&O itself puts in its gross premium beyond basic - NCD (its
// loading, less its discount), adds nothing for P&O's own add-on premiums, and replaces P&O's
// service tax and stamp duty with fixed values. The customer was charged it, while P&O bills its
// own totalPremium at issuance. The plan is now priced by P&O through the server, the same way the
// Cermat web app prices it, and the server records that quote for the /motor/store price floor.

export const POI_INSURER_NAME = "PACIFIC & ORIENT";

const DEFAULT_POI_ERROR = "Pacific & Orient could not price this quote. Please try again.";

export const isPoiPlan = (plan) =>
	String(plan?.insurer_name || "")
		.trim()
		.toUpperCase() === POI_INSURER_NAME;

const errorText = (value) => {
	if (typeof value === "string" && value.trim()) return value.trim();
	if (Array.isArray(value) && typeof value[0] === "string" && value[0].trim()) return value[0].trim();

	return "";
};

/**
 * Builds the transform for one server-priced insurer — P&O below, Great Eastern in
 * greatEasternQuote.js.
 *
 * It turns a /motor/get-quote-<insurer> reply (the backend helper's return value: the response body, or
 * the axios error on a non-2xx) into what the add-ons page stores as the plan.
 *
 * A priced reply keeps the listed card's display fields (logo, benefits, add-on catalogue) and
 * takes every money and reference field from P&O: total_payable, gross_premium, sst_premium,
 * stamp_duty, loading_amount, commission, premium_ref_no (P&O's requestId, which the server
 * recorded), quote_ref_no. The selected add-ons stay in the shape the checkout sends.
 *
 * Anything else — an error card, no positive total, a failed request — becomes
 * `{ success: false, data: { message } }`, which the page shows and does not proceed on. The
 * browser figure is never used as a fallback.
 */
export const serverPricedPlanFromQuote = (insurerName, defaultError) => (reply, selectedPlan, selectedAddOns = []) => {
	const body = reply?.response?.data || reply;
	const card = Array.isArray(body?.data) ? body.data[0] : body?.data;
	const total = Number.parseFloat(card?.total_payable);
	const priced = reply?.success === true && card && typeof card === "object" && !errorText(card.error_message) && total > 0;

	if (!priced) {
		return {
			success: false,
			// The insurer's reason, else an error envelope's message — never a success envelope's text or a
			// transport error's ("Network Error").
			data: { message: errorText(card?.error_message) || (body?.success === false ? errorText(body.message) : "") || defaultError },
		};
	}

	return {
		success: true,
		data: {
			...selectedPlan,
			...card,
			insurer_name: insurerName,
			extra_coverages: selectedAddOns,
			extra_coverages_list: selectedAddOns,
		},
	};
};

export const poiPlanFromQuote = serverPricedPlanFromQuote(POI_INSURER_NAME, DEFAULT_POI_ERROR);
